import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  Chip, Alert, CircularProgress, IconButton, Tooltip, TablePagination,
  MenuItem,
} from '@mui/material';
import { Add, Search, Edit, Block, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorService } from '@smartgrow/shared';

export const ContractorsPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [laborTypeFilter, setLaborTypeFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['contractors', page, laborTypeFilter, statusFilter, debouncedSearch],
    queryFn: () => contractorService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      laborType: laborTypeFilter === 'ALL' ? undefined : laborTypeFilter,
      active: statusFilter === 'ALL' ? undefined : statusFilter === 'ACTIVE',
    }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => contractorService.deleteContractor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contractors'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => contractorService.activateContractor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contractors'] }),
  });

  const filtered = data?.content ?? [];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">Contractors</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/contractors/new')}>
          Add Contractor
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by company, contact person, or specialty..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <TextField select size="small" label="Labor Type" value={laborTypeFilter}
            onChange={(e) => setLaborTypeFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="FORMAL">Formal</MenuItem>
            <MenuItem value="CASUAL_DAY_LABOR">Casual</MenuItem>
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load contractors.</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Contact Person</TableCell>
              <TableCell>Specialty</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Labor Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : filtered?.length ? (
              filtered.map((c, i) => (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/contractors/${c.id}`)}>
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell><Typography variant="body2" fontFamily="monospace">{c.contractorCode}</Typography></TableCell>
                  <TableCell><Typography variant="body2" fontWeight="medium">{c.companyName}</Typography></TableCell>
                  <TableCell>{c.contactPerson ?? '-'}</TableCell>
                  <TableCell>{c.specialty ?? '-'}</TableCell>
                  <TableCell>{c.phone ?? '-'}</TableCell>
                  <TableCell>{c.email ?? '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={c.laborType === 'CASUAL_DAY_LABOR' ? 'Casual' : 'Formal'}
                      color={c.laborType === 'CASUAL_DAY_LABOR' ? 'warning' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={c.isActive ? 'Active' : 'Inactive'} color={c.isActive ? 'success' : 'error'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/contractors/${c.id}/edit`); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {c.isActive ? (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (globalThis.confirm(`Deactivate ${c.companyName}?`)) {
                                deactivateMutation.mutate(c.id);
                              }
                            }}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              activateMutation.mutate(c.id);
                            }}
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No contractors found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.totalElements ?? 0}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>
    </Box>
  );
};
